/**
 * Per-cell price history (T387 M5): who / when / why / old to new, from the
 * union reader (GET /api/price-audit). Notes and actors render as TEXT NODES
 * only (sec #23 — a note of "<img onerror=...>" must render literally).
 */
import { useEffect, useState } from 'preact/hooks';
import { listPriceAudit, type ApiPriceAuditEntry } from '../../lib/actions.js';
import { euro, shortDate } from '../../lib/format.js';

export type AuditHistoryProps = {
  service: string;
  locationId: string;
  band: string;
  label: string;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; entries: ApiPriceAuditEntry[] };

export function AuditHistory({ service, locationId, band, label }: AuditHistoryProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    setState({ kind: 'loading' });
    listPriceAudit({ service, locationId, band })
      .then((entries) => setState({ kind: 'ready', entries }))
      .catch(() => setState({ kind: 'error' }));
  }, [service, locationId, band]);

  return (
    <div class="audit-trail">
      {state.kind === 'loading' && <p>Getting the history for {label} {band}.</p>}
      {state.kind === 'error' && <p>Loading the history failed. Close and try again.</p>}
      {state.kind === 'ready' && state.entries.length === 0 && (
        <p>No changes recorded for {label} {band} yet.</p>
      )}
      {state.kind === 'ready' && state.entries.length > 0 && (
        <ul>
          {state.entries.map((e) => (
            <li key={e.auditId}>
              {e.oldCents != null && e.newCents != null && `${euro(e.oldCents)} to ${euro(e.newCents)}`}
              {e.oldCents == null && e.newCents != null && `First price ${euro(e.newCents)}`}
              {' · '}
              <span class="audit-who">{e.actor}</span>
              {`, ${shortDate(e.at)}`}
              {e.sourceBookingId != null ? ` · from booking ${e.sourceBookingId}` : ''}
              {' · '}
              {e.note}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
